/* eslint-disable @typescript-eslint/no-empty-function */
import compact from 'lodash/compact';
import partition from 'lodash/partition';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { type MapDispatchToProps, type MapStateToProps, connect } from 'react-redux';

import { messages } from '../constants';
import * as D from '../deps';
import { getValidFeaturedContentIds } from '../utils';

import BackstageGrid from './BackstageGrid';
import ArtistFeaturedContentGridItem from './pages/BackstageArtist1/ArtistFeaturedContentGridItem';

const isPlaylistPandoraType = (pandoraId: string): boolean => {
  return (
    D.Utils.SourceType.getPandoraTypeFromPandoraId(pandoraId) ===
    D.Constants.SourceTypes.PandoraTypes.PLAYLIST
  );
};

const isStationPandoraType = (pandoraId: string): boolean => {
  return (
    D.Utils.SourceType.getPandoraTypeFromPandoraId(pandoraId) ===
    D.Constants.SourceTypes.PandoraTypes.STATION
  );
};

export interface IBackstageFeaturedContentStateProps {
  validFeaturedContentIds: ReturnType<typeof getValidFeaturedContentIds>;
}

export interface IBackstageFeaturedContentDispatchProps {
  annotatePandoraIds: $TSFixMeFunction;
  annotatePlaylists: typeof D.Actions.Playlist.annotatePlaylists;
  graphQLRequest: $TSFixMe;
}

export interface IBackstageFeaturedContentOwnProps {
  featuredContentIds: $TSFixMe;
  isFirstSection?: boolean;
}

export type IBackstageFeaturedContentProps = IBackstageFeaturedContentStateProps &
  IBackstageFeaturedContentDispatchProps &
  IBackstageFeaturedContentOwnProps;

const BackstageFeaturedContent: React.FC<IBackstageFeaturedContentProps> = props => {
  const {
    featuredContentIds,
    annotatePlaylists,
    annotatePandoraIds,
    validFeaturedContentIds,
    graphQLRequest,
  } = props;

  useEffect(() => {
    const [playlistOrStationEntities, restEntities] = partition(
      featuredContentIds.toJS(),
      pandoraId => isPlaylistPandoraType(pandoraId) || isStationPandoraType(pandoraId),
    );

    const [playlists, stations] = partition(playlistOrStationEntities, isPlaylistPandoraType);

    if (playlists.length) {
      annotatePlaylists({ pandoraIds: playlists });
      graphQLRequest(
        D.GraphQL.Queries.getPlaylistCurators({
          pandoraIds: compact(playlists),
        }),
      );
    }

    if (stations.length) {
      annotatePandoraIds(stations);
      graphQLRequest(
        D.GraphQL.Queries.getStationCurators({
          pandoraIds: compact(stations),
        }),
      );
    }

    if (restEntities.length) {
      annotatePandoraIds(restEntities);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotatePlaylists, annotatePandoraIds]);

  if (validFeaturedContentIds.size === 0) return null;

  return (
    <D.ContentSection
      isGridList={true}
      isFeaturedContent={true}
      isFirstSection={props.isFirstSection}
      title={<FormattedMessage {...messages.spotlight} />}
    >
      <BackstageGrid>
        {/* @ts-expect-error TS(7006) FIXME: Parameter 'pandoraId' implicitly has an 'any' type... Remove this comment to see the full error message */}
        {validFeaturedContentIds.map(pandoraId => (
          <ArtistFeaturedContentGridItem pandoraId={pandoraId} key={pandoraId} />
        ))}
      </BackstageGrid>
    </D.ContentSection>
  );
};

export const mapStateToProps: MapStateToProps<
  IBackstageFeaturedContentStateProps,
  IBackstageFeaturedContentOwnProps,
  $TSFixMe
> = (state, ownProps) => {
  const { featuredContentIds } = ownProps;
  return {
    validFeaturedContentIds: getValidFeaturedContentIds(state, featuredContentIds),
  };
};

const mapDispatchToProps: MapDispatchToProps<
  IBackstageFeaturedContentDispatchProps,
  IBackstageFeaturedContentOwnProps
> = {
  annotatePandoraIds: D.getInDomain('catalog.actions.annotateObjectsSimple', () => {}),
  annotatePlaylists: D.Actions.Playlist.annotatePlaylists,
  graphQLRequest: D.Services.GraphQL.graphQLRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(BackstageFeaturedContent);
